import { useEffect, useRef, useState } from 'react';
import { useReglages } from '../api/reglages.jsx';
import { iaApi, repriseApi } from '../api/resources';
import { prepareFichiers } from '../lib/document-ia';
import { tr } from '../lib/i18n';
import { litTexteTicket } from '../lib/lecture-ticket';
import { lignesDeGrossiste } from '../lib/lignes-grossiste';
import { lisPiece, sansCodesEnTete } from '../lib/reprise-texte';
import { texteDuJustificatif } from '../lib/ocr-ticket';
import { CLE_PACK_IA, packIaActif } from '../lib/pack-ia';
import { C } from '../theme/palette';
import { PhotoBtn } from './PhotoBtn.jsx';

/** Types qu'accepte la lecture d'une facture. */
export const TYPES_FACTURE = 'image/*,application/pdf';

/** Taille maximale d'une facture envoyée à la lecture, en octets. */
const OCTETS_MAX = 10 * 1024 * 1024;

const EN_COURS = 'en cours';

/** Lit un fichier en adresse de données, pour l'aperçu joint au formulaire. */
const enAdresse = (fichier) =>
  new Promise((resout, rejette) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resout(String(lecteur.result || ''));
    lecteur.onerror = () => rejette(new Error('Le fichier n’a pas pu être lu.'));
    lecteur.readAsDataURL(fichier);
  });

/**
 * La facture lue dans le téléphone, sans Pack IA : fournisseur et adresse,
 * numéro, date, total, TVA et IBAN. Le nom du fichier sert de numéro quand la
 * facture n'en imprime pas de lisible (« D2026-059.pdf »).
 *
 * @param {Blob & {name?: string}} fichier facture en photo ou en PDF
 * @param {object} [societe] société qui reçoit la facture : jamais son fournisseur
 * @returns {Promise<object>} la lecture, à la forme de celle du Pack IA
 */
async function litSurLeTelephone(fichier, societe) {
  const pdf = fichier.type === 'application/pdf' || /\.pdf$/i.test(fichier.name || '');
  // Un PDF se lit d'abord par le serveur, qui en sort le texte sur tous les
  // appareils et garde les colonnes du tableau. Lu seulement dans
  // l'iPhone, il échouait : « La facture n'a pas pu être lue », et ni
  // fournisseur, ni montant, ni date. Le téléphone reste là pour un PDF
  // scanné (sans texte), qu'il lit comme une photo, ou si le serveur ne
  // répond pas.
  let colonnes = '';
  if (pdf) {
    try {
      ({ texte: colonnes } = await repriseApi.texteDuPdf(fichier, fichier.name, 30000));
    } catch {
      colonnes = '';
    }
  }
  const avecTexte = String(colonnes || '').replace(/\s/g, '').length >= 20;
  // Le serveur a déjà été interrogé : sans texte, le téléphone lit seul.
  const texte = avecTexte ? colonnes : await texteDuJustificatif(fichier, { serveur: false });
  const lu = litTexteTicket(texte, {
    nousMemes: [societe?.nom].filter(Boolean),
    pays: societe?.pays,
  });
  const nom = String(fichier.name || '').replace(/\.[a-z0-9]+$/i, '');
  const numero =
    lu.numero || (/\d/.test(nom) && /^[A-Z0-9][A-Z0-9\-_/.]{2,20}$/i.test(nom) ? nom : null);
  // Les articles, lus comme ceux d'une ancienne offre reprise en PDF : la case
  // « Articles achetés » restait vide sans le Pack IA. Les colonnes du serveur
  // les gardent ; la lecture du téléphone colle la quantité au nom
  // (« Profilé CW 50 60 »).
  // Une facture de grossiste (Sabag…) : chaque article au prix net du rabais
  // de son groupe ; le lecteur des pièces prenait le récapitulatif.
  const deGrossiste = avecTexte ? lignesDeGrossiste(colonnes) : [];
  const lignes = deGrossiste.length
    ? deGrossiste
    : avecTexte
      ? lisPiece(colonnes)
          .lignes.filter((l) => l.d && +l.p > 0)
          // Un rabais sur la ligne (« 10% ») : le prix payé est le prix net,
          // celui qui compte dans le coût du chantier.
          .map((l) => ({
            // « 10 100142373 28030 kerakoll… » : la position et la référence du
            // fabricant tombent, le numéro d'article reste.
            designation: sansCodesEnTete(l.d),
            quantite: l.q,
            unite: l.u,
            prixUnitaire: +l.rem > 0 ? Math.round(l.p * (1 - l.rem / 100) * 100) / 100 : l.p,
          }))
      : [];
  return { ...lu, numero, lignes };
}

/**
 * « 📷 Scanner la facture » : joindre la photo, et la lire.
 *
 * Un seul bouton pour tous les écrans qui joignent une facture fournisseur
 * (chantier, sous-traitance, stock, ajout en lot, devis). La photo ou le PDF
 * part au Pack IA quand il est actif ; sans lui, ou s'il ne répond pas, la
 * facture est lue dans le téléphone — les champs restaient vides et le patron
 * devait tout recopier. Les champs lus reviennent dans le formulaire ;
 * l'utilisateur vérifie et enregistre comme d'habitude.
 *
 * @param {object} props propriétés du composant
 * @param {string} props.label libellé du bouton
 * @param {string} [props.color] couleur d'accent
 * @param {boolean} [props.camera] prise de vue en direct (photo seulement) plutôt que choix de fichier
 * @param {string} [props.capture] ouvre l'appareil photo du téléphone (`environment`)
 * @param {object} [props.style] style du conteneur
 * @param {object} [props.boutonStyle] style du bouton (sans caméra), ajouté au style par défaut
 * @param {(apercu: string, fichier: Blob) => void} [props.onPhoto] reçoit la pièce à joindre
 * @param {(lecture: object) => (string|void)} [props.onLecture] reçoit la facture lue ;
 *   peut rendre une remarque à afficher sous le bouton
 * @param {Blob} [props.aLire] fichier déjà choisi ailleurs (le bouton « Joindre » de
 *   l'offre) : joint et lu comme s'il venait de ce bouton
 * @returns {import('react').ReactElement} le bouton et l'état de la lecture
 */
export function ScanFacture({
  label,
  color = C.purple,
  camera = false,
  capture,
  style,
  boutonStyle,
  onPhoto,
  onLecture,
  sansBouton = false,
  aLire,
}) {
  const { reglageJson, societe } = useReglages();
  const actif = packIaActif(reglageJson(CLE_PACK_IA, null));
  const [etat, setEtat] = useState({ texte: '', erreur: false, alertes: [] });
  const dejaRecu = useRef(null);
  useEffect(() => {
    if (!aLire || dejaRecu.current === aLire || (!onLecture && !onPhoto)) return;
    dejaRecu.current = aLire;
    recoit(aLire);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aLire]);
  // Sans lecture ni pièce à joindre, le bouton n'aurait rien à faire.
  if (!onLecture && !onPhoto) return null;

  const lit = async (fichier) => {
    if (!onLecture || !fichier) return;
    if (fichier.size > OCTETS_MAX) {
      setEtat({
        texte: 'Ce fichier dépasse les 10 Mo admis pour la lecture.',
        erreur: true,
        alertes: [],
      });
      return;
    }
    setEtat({ texte: EN_COURS, erreur: false, alertes: [] });
    let lu = null;
    if (actif) {
      try {
        const fichiers = await prepareFichiers([fichier]);
        lu = await iaApi.lectureFacture({ fichiers });
      } catch {
        // Le Pack IA ne répond pas : la facture se lit dans le téléphone.
      }
    }
    // Le Pack IA a répondu sans le fournisseur ni le montant : la lecture du
    // téléphone complète ce qui manque, au lieu de laisser les cases vides.
    if (lu && (!lu.fournisseur || !(+lu.montantTtc > 0))) {
      try {
        const local = await litSurLeTelephone(fichier, societe);
        lu = {
          ...local,
          ...Object.fromEntries(
            Object.entries(lu).filter(
              ([, v]) => v != null && v !== '' && !(Array.isArray(v) && !v.length),
            ),
          ),
        };
      } catch {
        // La lecture du Pack IA reste.
      }
    }
    if (!lu) {
      try {
        lu = await litSurLeTelephone(fichier, societe);
      } catch {
        setEtat({
          texte: tr('La facture n’a pas pu être lue : complète le fournisseur et le montant.'),
          erreur: true,
          alertes: [],
        });
        return;
      }
    }
    // L'écran peut ajouter sa propre remarque : un article absent de la facture, par exemple.
    const remarque = onLecture(lu);
    setEtat({
      texte: '✓ Facture lue : vérifie les champs avant d’enregistrer.',
      erreur: false,
      alertes: [...(lu?.alertes || []), ...(typeof remarque === 'string' ? [remarque] : [])],
    });
  };

  const recoit = async (fichier) => {
    if (!fichier) return;
    if (onPhoto) {
      try {
        onPhoto(await enAdresse(fichier), fichier);
      } catch (e) {
        setEtat({ texte: e.message, erreur: true, alertes: [] });
        return;
      }
    }
    lit(fichier);
  };

  const enCours = etat.texte === EN_COURS;
  // La photo ouvre l'appareil ; la facture reçue en PDF (ou déjà dans le
  // téléphone) se joint depuis les fichiers. Sans ce second bouton, un PDF
  // ne pouvait pas être joint là où l'écran ouvre l'appareil photo.
  const depuisLesFichiers = (camera || capture) && (
    <label
      style={{
        display: 'block',
        boxSizing: 'border-box',
        width: '100%',
        marginTop: 6,
        border: '1px dashed ' + color,
        borderRadius: 8,
        padding: '9px',
        color,
        fontSize: 11,
        fontWeight: 700,
        textAlign: 'center',
        cursor: enCours ? 'wait' : 'pointer',
      }}
    >
      {tr('📎 Joindre un fichier (PDF ou photo)')}
      <input
        type="file"
        accept={TYPES_FACTURE}
        aria-label={tr('Facture à joindre')}
        disabled={enCours}
        style={{ display: 'none' }}
        onChange={(e) => {
          const fichier = e.target.files && e.target.files[0];
          e.target.value = '';
          recoit(fichier);
        }}
      />
    </label>
  );
  const libelle = enCours ? '📷 Lecture de la facture…' : label + (actif ? ' · lecture IA' : '');

  return (
    <div style={style}>
      {/* La photo prise, le bouton s'efface, mais la lecture reste visible. */}
      {sansBouton ? (
        enCours && (
          <div role="status" style={{ fontSize: 10.5, color: color, fontWeight: 700 }}>
            {libelle}
          </div>
        )
      ) : camera ? (
        <>
          <PhotoBtn
            label={libelle}
            color={color}
            full
            onPhoto={(apercu, fichier) => {
              onPhoto && onPhoto(apercu, fichier);
              lit(fichier);
            }}
          />
          {depuisLesFichiers}
        </>
      ) : (
        <>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              boxSizing: 'border-box',
              width: '100%',
              height: '100%',
              border: '1px dashed ' + color,
              borderRadius: 8,
              padding: '8px 9px',
              color,
              fontSize: 11,
              fontWeight: 700,
              textAlign: 'center',
              cursor: enCours ? 'wait' : 'pointer',
              ...boutonStyle,
            }}
          >
            {libelle}
            <input
              type="file"
              accept={TYPES_FACTURE}
              capture={capture}
              aria-label="Facture à scanner"
              disabled={enCours}
              style={{ display: 'none' }}
              onChange={(e) => {
                const fichier = e.target.files && e.target.files[0];
                e.target.value = '';
                recoit(fichier);
              }}
            />
          </label>
          {depuisLesFichiers}
        </>
      )}
      {etat.texte && !enCours && (
        <div
          role={etat.erreur ? 'alert' : 'status'}
          style={{
            fontSize: 10.5,
            color: etat.erreur ? C.red : C.green,
            marginTop: 5,
            lineHeight: 1.5,
          }}
        >
          {etat.texte}
        </div>
      )}
      {etat.alertes.map((a) => (
        <div key={a} style={{ fontSize: 10.5, color: C.amber, marginTop: 3, lineHeight: 1.5 }}>
          ⚠ {a}
        </div>
      ))}
    </div>
  );
}
